import { useEffect } from 'react';

import { getSignInProcessState } from 'handlers/curo-handlers/selectors';
import { AccountDataType } from 'handlers/curo-handlers/signInProcessSlice';
import { useAppSelector } from 'hooks/reduxHooks';
import { AppProcessSteps, useAppProgressBar } from 'hooks/useAppProgressBar';
import { useCurrentStep } from 'hooks/useCurrentStep';
import { FailedVerificationPage } from 'pages/CuroPages/EndPages';

import EmailVerificationStep from './EmailVerificationStep';
import PhoneVerificationStep from './PhoneVerificationStep';
import { VerificationProcessSteps } from './constants';

const getVerificationProcessStep = (
  accountData: AccountDataType,
): VerificationProcessSteps | null => {
  const isEmailVerified = accountData.isEmailVerified;
  const isPhoneVerified = accountData.isPhoneVerified;

  if (!isPhoneVerified) {
    return VerificationProcessSteps.PhoneVerificationStep;
  }

  if (!isEmailVerified) {
    return VerificationProcessSteps.EmailVerificationStep;
  }

  return null;
};

const VerificationProcessStep = () => {
  const { setProgressStep, resetProgress } = useAppProgressBar();
  const { accountData } = useAppSelector(getSignInProcessState);

  const [currentStep, setCurrentStep] = useCurrentStep<VerificationProcessSteps | null>(
    getVerificationProcessStep(accountData!),
  );

  useEffect(() => {
    const updateProgressBar = () => {
      switch (currentStep) {
        case VerificationProcessSteps.PhoneVerificationStep:
        case VerificationProcessSteps.EmailVerificationStep:
          setProgressStep(AppProcessSteps.VerificationStep);
          break;
        case VerificationProcessSteps.FailedVerificationStep:
          resetProgress();
          break;
        default:
          return;
      }
    };

    updateProgressBar();
  }, [currentStep]);

  const renderStep = () => {
    switch (currentStep) {
      case VerificationProcessSteps.PhoneVerificationStep:
        return (
          <PhoneVerificationStep
            onStepComplete={() => setCurrentStep(VerificationProcessSteps.EmailVerificationStep)}
            onStepFailed={() => setCurrentStep(VerificationProcessSteps.FailedVerificationStep)}
          />
        );
      case VerificationProcessSteps.EmailVerificationStep:
        return (
          <EmailVerificationStep
            onStepComplete={() => setCurrentStep(null)}
            onStepFailed={() => setCurrentStep(VerificationProcessSteps.FailedVerificationStep)}
          />
        );
      case VerificationProcessSteps.FailedVerificationStep:
        return <FailedVerificationPage />;
      default:
        return null as never;
    }
  };

  return renderStep();
};

export default VerificationProcessStep;
